import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Button from "@mui/material/Button";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import RowRoutesActionsMenu from "./RowRoutesActionsMenu";
import RowVillagesActionsMenu from "./RowVillagesActionsMenu";
import ActiveStatusCell from "../../../utils/ActiveStatusCell";
import { Grid } from "@mui/material";

export default function RoutesManagement() {
  const user = React.useMemo(() => {
    const u = sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [villageRows, setVillageRows] = useState([]);
  const [villageColumns, setVillageColumns] = useState([]);
  const [villageEditing, setVillageEditing] = useState(null);
  const [villageRouteName, setVillageRouteName] = useState("");
  const [villageFormData, setVillageFormData] = useState({
    village_name: "",
    village_desc: "",
    route_id: "",
    status: "ACTIVE",
    workflow_status: "",
    km: "",
    timings: "",
    oldfee_amount: "",
    newfee_amount: "",
    oldstrength: "",
    newstrength: "",
    availability: "Y",
    percent: "",
    concessionfee: "",
  });
  const [villageDialogOpen, setVillageDialogOpen] = useState(false);
  const [villageViewOpen, setVillageViewOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [acyearId, setAcyearId] = useState("");
  useEffect(() => {
    const fetchAcyear = async () => {
      if (!user || !user.client_id || !user.college_id) return;
      try {
        const url = `${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${user.college_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const dataArr = res.data?.data || [];
        const latest = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
        if (latest && latest.acyear_id) setAcyearId(latest.acyear_id);
      } catch (err) {
        setAcyearId("");
      }
    };
    fetchAcyear();
  }, [user, API_BASE_URL]);

  const openRouteVillageDialog = (route_id, routen_name) => {
    setVillageFormData({
      village_name: "",
      village_desc: "",
      route_id: route_id || "",
      status: "ACTIVE",
      workflow_status: "",
      km: "",
      timings: "",
      oldfee_amount: "",
      newfee_amount: "",
      oldstrength: "",
      newstrength: "",
      availability: "Y",
      percent: "",
      concessionfee: "",
    });
    setVillageEditing(null);
    setVillageRouteName(routen_name || "");
    setVillageDialogOpen(true);
  };

  const openRouteVillagesView = async (route_id, routen_name) => {
    if (!user || !user.client_id) {
      showSnackbar("User not loaded. Please login again.", "error");
      return;
    }
    try {
      const url = `${API_BASE_URL}/api/transport-villages/by-route/${user.client_id}/${user.college_id}/${route_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const filtered = Array.isArray(res.data) ? res.data : res.data?.data || [];

      setVillageColumns([
        { field: "village_id", headerName: "ID", flex: 0.3, minWidth: 60 },
        { field: "village_name", headerName: "Village Name", flex: 1, minWidth: 140 },
        { field: "newfee_amount", headerName: "New Fee Amount", flex: 0.7, minWidth: 120 },
        { field: "percent", headerName: "Percent", flex: 0.5, minWidth: 80 },
        { field: "concessionfee", headerName: "Concession Fee", flex: 0.7, minWidth: 120 },
        {
          field: "status",
          headerName: "Status",
          flex: 0.4,
          minWidth: 100,
          sortable: false,
          renderCell: (params) => (
            <ActiveStatusCell
              value={Number(params.row.delete_status || 0) === 0 ? "ACTIVE" : "INACTIVE"}
              onClick={async () => {
                const newDeleteStatus = Number(params.row.delete_status || 0) === 0 ? 1 : 0;
                try {
                  await axios.patch(
                    `${API_BASE_URL}/api/transport-villages/${user.client_id}/${params.row.village_id}/status`,
                    {
                      delete_status: newDeleteStatus,
                      modified_by: user.user_email || "system",
                    },
                    { headers: getAuthHeaders() }
                  );
                  showSnackbar("Status updated", "success");
                  openRouteVillagesView(route_id, routen_name);
                } catch (err) {
                  showSnackbar(err?.response?.data?.message || "Failed to update status", "error");
                }
              }}
            />
          ),
        },
        {
          field: "actions",
          headerName: "Actions",
          sortable: false,
          flex: 0.6,
          minWidth: 140,
          renderCell: (params) => (
            <RowVillagesActionsMenu
              row={params.row}
              onEdit={(r) => {
                setVillageEditing(r);
                setVillageFormData({
                  village_name: r.village_name || "",
                  route_id: r.route_id || route_id,
                  status: r.status || "ACTIVE",
                  workflow_status: r.workflow_status || "",
                  km: r.km || "",
                  timings: r.timings || "",
                  oldfee_amount: r.oldfee_amount || "",
                  newfee_amount: r.newfee_amount || "",
                  oldstrength: r.oldstrength || "",
                  newstrength: r.newstrength || "",
                  availability: r.availability || "Y",
                  percent: r.percent || "",
                  concessionfee: r.concessionfee || "",
                });
                setVillageRouteName(routen_name || "");
                setVillageDialogOpen(true);
              }}
              onDelete={async (id) => {
                if (!window.confirm("Are you sure you want to delete this village?")) return;
                try {
                  await axios.delete(
                    `${API_BASE_URL}/api/transport-villages/${user.client_id}/${id}`,
                    { headers: getAuthHeaders() }
                  );
                  showSnackbar("Village deleted", "success");
                  openRouteVillagesView(route_id, routen_name);
                } catch (err) {
                  showSnackbar(err?.response?.data?.message || "Failed to delete village", "error");
                }
              }}
              hasPermissionDirect={() => true}
            />
          ),
        },
      ]);
      setVillageRows(filtered || []);
      setVillageRouteName(routen_name || "");
      setVillageViewOpen(true);
    } catch (err) {
      showSnackbar("Failed to load villages", "error");
    }
  };

  const handleVillageSave = async () => {
    if (!villageFormData.village_name?.trim()) {
      showSnackbar("Village name is required", "error");
      return;
    }
    if (!acyearId) {
      showSnackbar("Academic year not loaded. Please try again.", "error");
      return;
    }
    if (!villageFormData.km || isNaN(Number(villageFormData.km))) {
      showSnackbar("Distance (km) is required and must be a number", "error");
      return;
    }
    if (!villageFormData.timings?.trim()) {
      showSnackbar("Timings are required", "error");
      return;
    }
    if (!villageFormData.oldfee_amount || isNaN(Number(villageFormData.oldfee_amount))) {
      showSnackbar("Old Fee Amount is required and must be a number", "error");
      return;
    }
    if (!villageFormData.newfee_amount || isNaN(Number(villageFormData.newfee_amount))) {
      showSnackbar("New Fee Amount is required and must be a number", "error");
      return;
    }
    if (!villageFormData.oldstrength || isNaN(Number(villageFormData.oldstrength))) {
      showSnackbar("Old Strength is required and must be a number", "error");
      return;
    }
    if (!villageFormData.newstrength || isNaN(Number(villageFormData.newstrength))) {
      showSnackbar("New Strength is required and must be a number", "error");
      return;
    }
    if (!villageFormData.percent || isNaN(Number(villageFormData.percent))) {
      showSnackbar("Percent is required and must be a number", "error");
      return;
    }
    if (!villageFormData.concessionfee || isNaN(Number(villageFormData.concessionfee))) {
      showSnackbar("Concession Fee is required and must be a number", "error");
      return;
    }
    try {
      const payload = {
        village_name: villageFormData.village_name,
        route_id: Number(villageFormData.route_id),
        created_by: user.user_email || "system",
        acyear_id: Number(acyearId),
        km: Number(villageFormData.km),
        timings: villageFormData.timings,
        oldfee_amount: Number(villageFormData.oldfee_amount),
        newfee_amount: Number(villageFormData.newfee_amount),
        oldstrength: Number(villageFormData.oldstrength),
        newstrength: Number(villageFormData.newstrength),
        availability: villageFormData.availability,
        percent: Number(villageFormData.percent),
        concessionfee: Number(villageFormData.concessionfee),
      };
      let res;
      if (villageEditing && (villageEditing.village_id || villageEditing.id)) {
        // Update existing village
        payload.updated_by = user.user_email || "system";
        // Do NOT include route_id in update
        const vid = villageEditing.village_id || villageEditing.id;
        res = await axios.put(
          `${API_BASE_URL}/api/transport-villages/${user.client_id}/${vid}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new village
        res = await axios.post(
          `${API_BASE_URL}/api/transport-villages/${user.client_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      showSnackbar(
        res.data.message || (villageEditing ? "Village updated" : "Village created"),
        "success"
      );
      setVillageDialogOpen(false);
      setVillageEditing(null);
      if (villageFormData.route_id) {
        openRouteVillagesView(villageFormData.route_id, villageRouteName);
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error saving village", "error");
    }
  };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [routeFormData, setRouteFormData] = useState({
    routen_name: "",
    max_capacity: "",
    current_enrolled: "",
  });

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const openActionsMenu = Boolean(actionsAnchorEl);

  // Common UI state
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Route -> Villages modal state (removed unused PBViewOpen logic)

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/transport-routes/${user.client_id}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(res.data?.data)) data = res.data.data;
      else if (Array.isArray(res.data)) data = res.data;
      setAllRows(data || []);
      setRows(data || []);
    } catch (err) {
      showSnackbar("Failed to load routes", "error");
      setAllRows([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  useEffect(() => {
    let filtered = [...allRows];
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter((r) => (r.routen_name || "").toLowerCase().includes(s));
    }
    setRows(filtered);
  }, [search, allRows]);

  const handleEdit = (row) => {
    setEditing(row);
    setRouteFormData({
      routen_name: row.routen_name || "",
      max_capacity: row.max_capacity || "",
      current_enrolled: row.current_enrolled || "",
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this route?")) return;
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/transport-routes/${user.client_id}/${id}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar(res.data.message || "Route deleted", "success");
      fetchData();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error deleting route", "error");
    }
  };

  // Actions menu handlers
  const handleActionsClick = (e) => setActionsAnchorEl(e.currentTarget);
  const handleActionsClose = () => setActionsAnchorEl(null);

  // CSV Download function (attempt server endpoint, fallback to client CSV)
  const handleDownloadCsv = async () => {
    try {
      // If filters/search are active, generate CSV from currently filtered rows
      const filtersActive = Boolean(search);
      if (filtersActive) {
        const headers = [
          "program_id",
          "program_name",
          "program_desc",
          "program_duration",
          "program_start_year",
          "is_program_active",
        ];
        const csvRows = [headers.join(",")];
        rows.forEach((r) => {
          const row = headers
            .map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`)
            .join(",");
          csvRows.push(row);
        });
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", "programs.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        showSnackbar("CSV downloaded successfully", "success");
        return;
      }

      const url = `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}/download/csv`;
      const res = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "programs.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded successfully", "success");
    } catch (err) {
      showSnackbar("Error downloading CSV", "error");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      // If any filters/search are active, generate PDF client-side from filtered rows
      const filtersActive = Boolean(search);
      if (filtersActive) {
        // dynamic import to keep bundle size small
        const { jsPDF } = await import("jspdf");
        await import("jspdf-autotable");

        const doc = new jsPDF();
        const headers = ["ID", "Program Name", "Description", "Duration", "Start Year", "Active"];
        const dataRows = rows.map((r) => [
          r.program_id || "",
          r.program_name || "",
          r.program_desc || "",
          r.program_duration || "",
          r.program_start_year || "",
          r.is_program_active == 1 ? "ACTIVE" : "INACTIVE",
        ]);

        doc.autoTable({ head: [headers], body: dataRows, startY: 10 });
        doc.save("programs.pdf");
        showSnackbar("PDF downloaded successfully", "success");
        return;
      }

      // No filters active: prefer server-generated PDF (streamed)
      const url = `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}/download/pdf`;
      const res = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "programs.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("PDF downloaded successfully", "success");
    } catch (err) {
      showSnackbar("Error downloading PDF", "error");
    }
  };

  const handleUploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}/upload/csv`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      showSnackbar(data.message || "CSV uploaded successfully", "success");
      fetchData();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Upload failed", "error");
    } finally {
      e.target.value = "";
    }
  };

  const [programFormData, setProgramFormData] = useState({
    program_name: "",
    program_desc: "",
    is_program_active: 1,
    program_duration: "",
    program_start_year: "",
    workflow_status: "",
  });

  useEffect(() => {
    if (editing) {
      setProgramFormData({
        program_name: editing.program_name || "",
        program_desc: editing.program_desc || "",
        is_program_active: editing.is_program_active || 1,
        program_duration: editing.program_duration || "",
        program_start_year: editing.program_start_year || "",
        workflow_status: editing.workflow_status || "",
      });
    } else {
      setProgramFormData({ program_name: "", program_desc: "", is_program_active: 1 });
    }
  }, [editing]);

  const handleSubmit = async () => {
    if (!programFormData.program_name?.trim()) {
      showSnackbar("Program name is required", "error");
      return;
    }
    try {
      let res;
      const payload = {
        ...programFormData,
        college_id: user.college_id,
        created_by: user.user_email || "system",
      };

      if (editing) {
        res = await axios.put(
          `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}/${editing.program_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        res = await axios.post(
          `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      showSnackbar(res.data.message || (editing ? "Updated" : "Created"), "success");
      setOpen(false);
      setEditing(null);
      fetchData();
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error saving program", "error");
    }
  };

  const columns = [
    { field: "route_id", headerName: "ID", flex: 0.4, minWidth: 60 },
    { field: "routen_name", headerName: "Route Name", flex: 1.2, minWidth: 180 },
    { field: "max_capacity", headerName: "Max Capacity", flex: 0.8, minWidth: 120 },
    { field: "current_enrolled", headerName: "Current Enrolled", flex: 0.8, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <ActiveStatusCell
          value={Number(params.row.delete_status || 0) === 0 ? "ACTIVE" : "INACTIVE"}
          onClick={async () => {
            const newDeleteStatus = Number(params.row.delete_status || 0) === 0 ? 1 : 0;
            try {
              await axios.patch(
                `${API_BASE_URL}/api/transport-routes/${user.client_id}/${params.row.route_id}/status`,
                {
                  delete_status: newDeleteStatus,
                  modified_by: user.user_email || "system",
                },
                { headers: getAuthHeaders() }
              );
              showSnackbar("Status updated", "success");
              fetchData();
            } catch (err) {
              showSnackbar(err?.response?.data?.message || "Failed to update status", "error");
            }
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <RowRoutesActionsMenu
          row={params.row}
          onEdit={(r) => handleEdit(r)}
          onDelete={(id) => handleDelete(id)}
          onCreateRouteVillage={openRouteVillageDialog}
          onViewRouteVillages={openRouteVillagesView}
          hasPermissionDirect={() => true}
        />
      ),
    },
  ];

  // Place the Create Villages and View Villages Dialogs at the top level, outside the columns array
  return (
    <>
      <Box p={2}>
        <Paper elevation={1}>
          <Toolbar sx={{ gap: 1 }}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              ROUTE MANAGEMENT
            </Typography>

            <Button
              variant="contained"
              startIcon={<MoreVertIcon />}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
                "&:hover": { backgroundColor: "#115293", color: "#fff" },
              }}
              onClick={handleActionsClick}
            >
              ACTIONS
            </Button>
            <Menu anchorEl={actionsAnchorEl} open={openActionsMenu} onClose={handleActionsClose}>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    textTransform: "none",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
                    "&:hover": { backgroundColor: "#115293", color: "#fff" },
                  }}
                  onClick={() => {
                    handleActionsClose();
                    setOpen(true);
                  }}
                >
                  ADD ROUTE
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#357a38", color: "#fff" },
                  }}
                  onClick={() => {
                    handleActionsClose();
                    handleDownloadCsv();
                  }}
                >
                  DOWNLOAD CSV
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  sx={{
                    backgroundColor: "#e53935",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#c62828", color: "#fff" },
                  }}
                  onClick={() => {
                    handleActionsClose();
                    handleDownloadPdf();
                  }}
                >
                  DOWNLOAD PDF
                </Button>
              </MenuItem>
              <MenuItem disableRipple>
                <Button
                  fullWidth
                  variant="contained"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    backgroundColor: "#ff9800",
                    color: "#fff",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#c77700", color: "#fff" },
                  }}
                >
                  UPLOAD CSV
                  <input
                    hidden
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      handleActionsClose();
                      handleUploadCsv(e);
                    }}
                  />
                </Button>
              </MenuItem>
            </Menu>
            <TextField
              size="medium"
              placeholder="Search routes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Toolbar>

          <Box sx={{ height: 560, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(r) => r.route_id}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e0e0e0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                  borderBottom: "2px solid #e0e0e0",
                },
              }}
            />
          </Box>
        </Paper>

        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editing ? "Edit Route" : "Add Route"}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Route Name"
                required
                fullWidth
                value={routeFormData.routen_name}
                onChange={(e) =>
                  setRouteFormData({ ...routeFormData, routen_name: e.target.value })
                }
              />
              <TextField
                label="Max Capacity"
                type="number"
                fullWidth
                value={routeFormData.max_capacity}
                onChange={(e) =>
                  setRouteFormData({ ...routeFormData, max_capacity: e.target.value })
                }
              />
              <TextField
                label="Current Enrolled"
                type="number"
                fullWidth
                value={routeFormData.current_enrolled}
                onChange={(e) =>
                  setRouteFormData({ ...routeFormData, current_enrolled: e.target.value })
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (!routeFormData.routen_name?.trim()) {
                  showSnackbar("Route name is required", "error");
                  return;
                }
                try {
                  let res;
                  const payload = {
                    routen_name: routeFormData.routen_name, // <-- match DB column
                    max_capacity: Number(routeFormData.max_capacity),
                    current_enrolled: Number(routeFormData.current_enrolled),
                    created_by: user.user_email || "system",
                  };
                  if (editing && (editing.route_id || editing.id)) {
                    // Update existing route
                    res = await axios.put(
                      `${API_BASE_URL}/api/transport-routes/${user.client_id}/${
                        editing.route_id || editing.id
                      }`,
                      payload,
                      { headers: getAuthHeaders() }
                    );
                  } else {
                    // Create new route
                    res = await axios.post(
                      `${API_BASE_URL}/api/transport-routes/${user.client_id}`,
                      payload,
                      { headers: getAuthHeaders() }
                    );
                  }
                  showSnackbar(
                    res.data.message || (editing ? "Route updated" : "Route created"),
                    "success"
                  );
                  setOpen(false);
                  setEditing(null);
                  fetchData();
                } catch (err) {
                  showSnackbar(err?.response?.data?.message || "Error saving route", "error");
                }
              }}
              sx={{
                ml: 2,
                color: "#fff",
                "&:hover": { color: "#fff" },
                "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
              }}
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>

      <Dialog
        open={villageViewOpen}
        onClose={() => setVillageViewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Villages for Route: {villageRouteName}
          <IconButton
            onClick={() => setVillageViewOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={villageRows}
              columns={villageColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              getRowId={(r) => r.village_id || r.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="success" onClick={() => setVillageViewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={villageDialogOpen}
        onClose={() => setVillageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {villageEditing ? "Edit Village" : `Add Village for ${villageRouteName}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Village Name"
                  required
                  fullWidth
                  value={villageFormData.village_name}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, village_name: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Distance (km)"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.km}
                  onChange={(e) => setVillageFormData({ ...villageFormData, km: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Timings"
                  required
                  fullWidth
                  value={villageFormData.timings}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, timings: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Old Fee Amount"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.oldfee_amount}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, oldfee_amount: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Fee Amount"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.newfee_amount}
                  onChange={(e) => {
                    const newfee = e.target.value;
                    const percent = villageFormData.percent;
                    let concessionfee = villageFormData.concessionfee;
                    if (newfee && percent && !isNaN(Number(newfee)) && !isNaN(Number(percent))) {
                      concessionfee = ((Number(newfee) * Number(percent)) / 100).toFixed(2);
                    }
                    setVillageFormData({
                      ...villageFormData,
                      newfee_amount: newfee,
                      concessionfee: concessionfee,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Old Strength"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.oldstrength}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, oldstrength: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Strength"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.newstrength}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, newstrength: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Availability (Y/N)"
                  required
                  fullWidth
                  value={villageFormData.availability}
                  onChange={(e) =>
                    setVillageFormData({ ...villageFormData, availability: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Percent"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.percent}
                  onChange={(e) => {
                    const percent = e.target.value;
                    const newfee = villageFormData.newfee_amount;
                    let concessionfee = villageFormData.concessionfee;
                    if (newfee && percent && !isNaN(Number(newfee)) && !isNaN(Number(percent))) {
                      concessionfee = ((Number(newfee) * Number(percent)) / 100).toFixed(2);
                    }
                    setVillageFormData({
                      ...villageFormData,
                      percent: percent,
                      concessionfee: concessionfee,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Concession Fee"
                  required
                  fullWidth
                  type="number"
                  value={villageFormData.concessionfee}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  margin="normal"
                  label="Workflow Status"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={villageFormData.workflow_status}
                  onChange={(e) =>
                    setVillageFormData((prev) => ({
                      ...prev,
                      workflow_status: e.target.value,
                    }))
                  }
                  SelectProps={{ native: true }}
                  helperText="Select workflow status for village"
                >
                  <option value="">Select Workflow Status</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="DRAFT">DRAFT</option>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVillageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleVillageSave}
            sx={{
              ml: 2,
              backgroundColor: "#1976d2",
              color: "#fff",
              "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
              "&:hover": { backgroundColor: "#115293", color: "#fff" },
            }}
          >
            {villageEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
